import { FC } from 'react';

import { Button, FormLayout, Heading, LoadingDots, Paragraph, SubmitButton } from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEvaluationCalendar } from 'domains/calendars/v3/useEvaluationCalendar';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import {
  SingleEvaluationSchema,
  SingleEvaluationSchemaType,
} from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useSplitEvaluationCtx } from '../SplitEvaluationsScheduleSection';
import { useSignupSecondSplitCalendarData } from './hooks';
import { SecondSplitTimeSlotPicker } from './Picker';
import { useMinimumAvailability } from 'domains/signupDirect/misc/useMinimumAvailability';

export const EvaluationScheduleSecondSplit = () => {
  useAutoScrollToTop();
  const { t } = useTranslation();
  const { track } = useTrack();
  const { onSubmitSecondSlot, onSkipSecondSlot } = useSplitEvaluationCtx();
  const { isLoading } = useSignupSecondSplitCalendarData();

  const methods = useForm<SingleEvaluationSchemaType>({
    resolver: zodResolver(SingleEvaluationSchema),
    defaultValues: {
      type: 'single',
      timeZone: '',
    },
  });

  const { handleSubmit } = methods;

  if (isLoading) {
    return <LoadingDots className="flex flex-col h-max static mt-48" message={t('directSignup.schedule.loading')} />;
  }
  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.schedule.split.second.title')}</Heading>
        <Paragraph size="lg" noDefaultMargins>
          {t('directSignup.schedule.split.second.subTitle')}
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout
          className="mobile-xs-d:gap-e40 tablet-d:gap-e56 pb-e128"
          onSubmit={handleSubmit(d => {
            onSubmitSecondSlot(d.timeSlot);
          })}
        >
          <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
            <FormFields />
          </FormLayout.Fields>

          <FormLayout.CTA sticky>
            <SubmitButton data-testid="picktime-submit-button">{t('directSignup.eval.schedule.submit')}</SubmitButton>
            <Button
              variant="link"
              onClick={() => {
                track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SKIP_SECOND_SPLIT);
                onSkipSecondSlot();
              }}
            >
              {t('directSignup.schedule.split.second.skip')}
            </Button>
          </FormLayout.CTA>
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields: FC = () => {
  const { filters, isResumedLead } = useSignupDirectCtx();
  const { firstTimeSlot, onSkipSecondSlot } = useSplitEvaluationCtx();
  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SPLIT_SECOND, { isResumedLead });

  const query = useSignupSecondSplitCalendarData();
  useMinimumAvailability({
    evaluationCalendarQuery: query,
    onLowAvailability: onSkipSecondSlot,
    disabled: true,
  });

  if (!filters) {
    throw 'Expected filters to be set';
  }
  if (!firstTimeSlot) {
    throw 'Expected firstTimeSlot to be set';
  }

  const data = useEvaluationCalendar({ query });

  if (data.isLoading) throw new Error('Expected data to be loaded SecondSplit');

  return (
    <SecondSplitTimeSlotPicker
      calendarData={data}
      name="timeSlot"
      firstSessionTherapistId={firstTimeSlot.therapistID}
    />
  );
};
